/**
 * CSV serialization for report blocks. Pure data → string; the caller
 * decides how to deliver it (download, attach to email, push to S3 etc).
 *
 * Rules: RFC 4180 quoting. Values containing `,`, `"`, `\n`, or `\r` are
 * wrapped in double quotes and internal quotes are doubled. Booleans
 * stringify to "true"/"false". `null` and `undefined` render as empty
 * strings. Numbers render with their default toString (no locale).
 */
import { humanizeAny, formatValue } from "../labels";
import type { NumberFormat } from "../schema";
import type { QueryResult } from "../adapters/types";

export interface CsvColumnHint {
  key: string;
  label?: string;
  format?: NumberFormat;
}

export interface RowsToCsvOptions {
  /** Per-column overrides — when omitted, columns are derived from the
   *  first row of rows[] in insertion order. */
  columns?: CsvColumnHint[];
  /** When true, numeric / percent / duration values are formatted using
   *  the same humanised formatter as the on-screen table. Default: false
   *  (raw numbers — better for spreadsheet re-aggregation). */
  formatValues?: boolean;
  /** Override the header row entirely. Same length as columns. */
  headers?: string[];
}

function quoteField(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "boolean") return value ? "true" : "false";
  const str = typeof value === "string" ? value : String(value);
  if (/[",\r\n]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

export function rowsToCsv(
  rows: Array<Record<string, unknown>>,
  options: RowsToCsvOptions = {},
): string {
  if (rows.length === 0 && (!options.columns || options.columns.length === 0)) {
    return "";
  }

  const derivedCols: CsvColumnHint[] =
    options.columns ?? Object.keys(rows[0] ?? {}).map((k) => ({ key: k }));

  const headerLabels =
    options.headers ??
    derivedCols.map((c) => (c.label ? humanizeAny(c.label) : humanizeAny(c.key)));

  const headerLine = headerLabels.map(quoteField).join(",");

  const bodyLines = rows.map((row) =>
    derivedCols
      .map((col) => {
        const raw = row[col.key];
        if (options.formatValues && col.format != null && typeof raw === "number") {
          return quoteField(formatValue(raw, col.format));
        }
        return quoteField(raw);
      })
      .join(","),
  );

  return [headerLine, ...bodyLines].join("\r\n") + "\r\n";
}

/**
 * Adapter-friendly: take a QueryResult straight from the compiler and
 * serialize it using its column metadata (so headers + formats come for
 * free).
 */
export function queryResultToCsv(
  result: QueryResult,
  options: Omit<RowsToCsvOptions, "columns"> = {},
): string {
  const columns: CsvColumnHint[] = result.columns.map((c) => ({
    key: c.key,
    label: c.label,
    format: c.format,
  }));
  return rowsToCsv(result.rows as Array<Record<string, unknown>>, {
    ...options,
    columns,
  });
}

/**
 * Browser-only: trigger a CSV download. Returns the same csv string so
 * callers can also pipe it elsewhere.
 */
export function downloadCsv(csv: string, filename: string): string {
  if (typeof document === "undefined") return csv;
  // Prefix BOM so Excel opens UTF-8 correctly.
  const blob = new Blob(["﻿", csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename.endsWith(".csv") ? filename : `${filename}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  return csv;
}
